import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { User, Building2, Stethoscope, Sparkles, ArrowRight, ArrowLeft, X, Check } from "lucide-react";

const SPECIALTIES = [
  "General Medicine", "Cardiology", "Pediatrics", "Gynecology", "Orthopedics",
  "Dermatology", "ENT", "Ophthalmology", "Neurology", "Psychiatry",
  "Endocrinology", "Gastroenterology", "Nephrology", "Pulmonology", "Urology",
  "Oncology", "Dentistry", "Surgery", "Radiology", "Anesthesiology",
];

type FormState = {
  first_name: string;
  last_name: string;
  phone: string;
  degrees: string;
  bmdc_reg_no: string;
  years_experience: string;
  bio: string;
  clinic_name: string;
  clinic_address: string;
  city: string;
  consultation_fee: string;
  specialization: string;
  specialty_tags: string[];
};

const initialForm: FormState = {
  first_name: "", last_name: "", phone: "", degrees: "", bmdc_reg_no: "",
  years_experience: "", bio: "", clinic_name: "", clinic_address: "", city: "",
  consultation_fee: "", specialization: "", specialty_tags: [],
};

export default function OnboardingWizard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(initialForm);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user || checked) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      setChecked(true);
      if (data && !data.onboarding_completed) {
        setForm({
          first_name: data.first_name ?? "",
          last_name: data.last_name ?? "",
          phone: data.phone ?? "",
          degrees: data.degrees ?? "",
          bmdc_reg_no: data.bmdc_reg_no ?? "",
          years_experience: data.years_experience?.toString() ?? "",
          bio: data.bio ?? "",
          clinic_name: data.clinic_name ?? "",
          clinic_address: data.clinic_address ?? "",
          city: data.city ?? "",
          consultation_fee: data.consultation_fee?.toString() ?? "",
          specialization: data.specialization ?? "",
          specialty_tags: data.specialty_tags ?? [],
        });
        setOpen(true);
      }
    })();
  }, [user, checked]);

  const update = (k: keyof FormState, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (tag: string) => {
    const has = form.specialty_tags.includes(tag);
    update("specialty_tags", has ? form.specialty_tags.filter((t) => t !== tag) : [...form.specialty_tags, tag]);
  };

  const steps = [
    { title: "Your profile", icon: User, description: "Tell us who you are" },
    { title: "Clinic details", icon: Building2, description: "Where do you practice?" },
    { title: "Specialties", icon: Stethoscope, description: "Your areas of expertise" },
    { title: "All set", icon: Sparkles, description: "You're ready to go" },
  ];

  const canNext = () => {
    if (step === 0) return form.first_name.trim().length > 0;
    if (step === 1) return form.clinic_name.trim().length > 0;
    if (step === 2) return !!form.specialization || form.specialty_tags.length > 0;
    return true;
  };

  const handleFinish = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        phone: form.phone || null,
        degrees: form.degrees || null,
        bmdc_reg_no: form.bmdc_reg_no || null,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        bio: form.bio || null,
        clinic_name: form.clinic_name || null,
        clinic_address: form.clinic_address || null,
        city: form.city || null,
        consultation_fee: form.consultation_fee ? Number(form.consultation_fee) : null,
        specialization: form.specialization || null,
        specialty_tags: form.specialty_tags,
        onboarding_completed: true,
      })
      .eq("user_id", user.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Welcome to DoctorsX!");
    setOpen(false);
  };

  const handleSkip = async () => {
    if (!user) return;
    await supabase.from("profiles").update({ onboarding_completed: true }).eq("user_id", user.id);
    setOpen(false);
  };

  const StepIcon = steps[step].icon;
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && setOpen(v)}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden rounded-2xl">
        <div className="bg-gradient-to-br from-primary to-primary-glow p-6 text-primary-foreground relative">
          <button
            onClick={handleSkip}
            className="absolute right-4 top-4 text-primary-foreground/70 hover:text-primary-foreground text-xs flex items-center gap-1"
          >
            Skip <X className="h-3 w-3" />
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <StepIcon className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg text-primary-foreground">{steps[step].title}</DialogTitle>
              <DialogDescription className="text-primary-foreground/80 text-sm">
                {steps[step].description}
              </DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Progress value={progress} className="h-1.5 bg-white/20 flex-1" />
            <span className="text-xs text-primary-foreground/80">Step {step + 1} of {steps.length}</span>
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {step === 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First name *</Label>
                  <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} placeholder="Dr. Rahim" />
                </div>
                <div className="space-y-2">
                  <Label>Last name</Label>
                  <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} placeholder="Uddin" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+8801XXXXXXXXX" />
                </div>
                <div className="space-y-2">
                  <Label>BMDC registration</Label>
                  <Input value={form.bmdc_reg_no} onChange={(e) => update("bmdc_reg_no", e.target.value)} placeholder="A-12345" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Degrees</Label>
                <Input value={form.degrees} onChange={(e) => update("degrees", e.target.value)} placeholder="MBBS, FCPS (Medicine)" />
              </div>
              <div className="space-y-2">
                <Label>Short bio</Label>
                <Textarea rows={3} value={form.bio} onChange={(e) => update("bio", e.target.value)} placeholder="Consultant physician with focus on..." />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Clinic / chamber name *</Label>
                <Input value={form.clinic_name} onChange={(e) => update("clinic_name", e.target.value)} placeholder="Green Life Medical Center" />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea rows={2} value={form.clinic_address} onChange={(e) => update("clinic_address", e.target.value)} placeholder="House 5, Road 3, Dhanmondi" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Dhaka" />
                </div>
                <div className="space-y-2">
                  <Label>Consultation fee (৳)</Label>
                  <Input type="number" value={form.consultation_fee} onChange={(e) => update("consultation_fee", e.target.value)} placeholder="800" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Years of experience</Label>
                <Input type="number" value={form.years_experience} onChange={(e) => update("years_experience", e.target.value)} placeholder="10" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Primary specialization *</Label>
                <Input value={form.specialization} onChange={(e) => update("specialization", e.target.value)} placeholder="e.g. Cardiology" />
              </div>
              <div className="space-y-2">
                <Label>Additional specialty tags</Label>
                <p className="text-xs text-muted-foreground">Pick any that apply — they help patients find you.</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {SPECIALTIES.map((s) => {
                    const active = form.specialty_tags.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleTag(s)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                          active
                            ? "bg-primary text-primary-foreground border-primary shadow-sm"
                            : "bg-background border-border hover:border-primary/60"
                        }`}
                      >
                        {active && <Check className="inline h-3 w-3 mr-1" />}
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-8 space-y-4">
              <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">You're all set, Dr. {form.first_name}!</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Your profile is ready. You can update these details anytime from Settings.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 pt-4 text-left">
                <div className="p-3 rounded-lg border bg-muted/40">
                  <p className="text-[10px] uppercase text-muted-foreground">Clinic</p>
                  <p className="text-sm font-medium truncate">{form.clinic_name || "—"}</p>
                </div>
                <div className="p-3 rounded-lg border bg-muted/40">
                  <p className="text-[10px] uppercase text-muted-foreground">Specialty</p>
                  <p className="text-sm font-medium truncate">{form.specialization || "—"}</p>
                </div>
                <div className="p-3 rounded-lg border bg-muted/40">
                  <p className="text-[10px] uppercase text-muted-foreground">Tags</p>
                  <p className="text-sm font-medium">{form.specialty_tags.length}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-muted/30">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || saving}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()}>
              Continue <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleFinish} disabled={saving}>
              {saving ? "Saving..." : "Finish setup"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
